import { createContext, useContext, type JSX } from 'solid-js';
import { createStore } from 'solid-js/store';
import type { Bootstrap, BootstrapUser } from '../types';

interface BootstrapState {
    user: BootstrapUser | null;
    site: Bootstrap['site'] | null;
    initial: Bootstrap | null;
    /**
     * Set to true after a page has consumed `initial.data` via
     * `useInitialData()`. The bootstrap is only valid for the very first
     * paint after the server-rendered HTML; any client-side navigation
     * must fall through to a real fetch even if the new page's route
     * happens to match the original bootstrap route.
     */
    consumed: boolean;
}

const defaultState: BootstrapState = {
    user: null,
    site: null,
    initial: null,
    consumed: false,
};

const Ctx = createContext<ReturnType<typeof createStore<BootstrapState>>>();

export function BootstrapProvider(props: {
    value?: Bootstrap;
    children: JSX.Element;
}) {
    const store = createStore<BootstrapState>({
        user: props.value?.user ?? defaultState.user,
        site: props.value?.site ?? defaultState.site,
        initial: props.value ?? defaultState.initial,
        consumed: false,
    });
    return <Ctx.Provider value={store}>{props.children}</Ctx.Provider>;
}

export function useBootstrap() {
    const ctx = useContext(Ctx);
    if (!ctx) throw new Error('useBootstrap outside BootstrapProvider');
    return ctx;
}

/**
 * Pages should call this instead of `useBootstrap()` for resource seeding.
 * Returns the server-rendered payload only when:
 *   - bootstrap.route matches the requested page, AND
 *   - the payload has not yet been consumed (i.e. this is still the initial
 *     paint of the first page the user landed on).
 *
 * After the first page reads it the flag flips to consumed=true; subsequent
 * client-side navigations always do a clean fetch instead of being seeded
 * with stale data from a previous page.
 */
export function useInitialData<T>(routeName: string): T | undefined {
    const [state, setState] = useBootstrap();
    if (state.consumed) return undefined;
    if (!state.initial || state.initial.route !== routeName) return undefined;
    const data = state.initial.data as T | undefined;
    setState('consumed', true);
    return data;
}

/** The signed-in user chose 4K renditions for the lightbox (profile setting). */
export function usePrefers4k(): boolean {
    const [state] = useBootstrap();
    return !!state.user?.settings?.use4k;
}

/**
 * Wrap a createResource fetcher so its first call answers with the
 * server-seeded payload (when there is one) instead of fetching it again:
 * Solid runs a resource's fetcher on creation even when it has an
 * initialValue, which made every server-rendered page download its data
 * twice. Later calls (param changes, refetches) fetch normally.
 *
 *     const seed = useInitialData<ApiHome>('home');
 *     createResource(seededFetcher(seed, fetchHome), { initialValue: seed });
 */
export function seededFetcher<A extends unknown[], T>(
    seed: T | undefined,
    fetcher: (...args: A) => Promise<T>,
): (...args: A) => T | Promise<T> {
    let pending = seed;
    return (...args) => {
        if (pending !== undefined) {
            const value = pending;
            pending = undefined;
            return value;
        }
        return fetcher(...args);
    };
}
